import { useSyncExternalStore } from 'react';
import uuid from 'uuid/v4.js';

import { keys } from 'dibs-ts-utils/exports/keys.ts';

import { type ToastProps } from './Toast.tsx';

type ToastType = Omit<ToastProps, 'isVisible'>;

type ToastState = {
    toasts: (ToastType & { id: string; isVisible: boolean })[];
    lastToastAction: string | null;
};

type ToastActions = {
    add: ({ toast, toastAction }: { toast: ToastType; toastAction?: string }) => void;
    remove: (id: string) => void;
};

type ToastStore = ToastState & ToastActions;

type Listener = (state: ToastStore, prevState: ToastStore) => void;

type SetState = (
    partial: Partial<ToastStore> | ((state: ToastStore) => Partial<ToastStore>)
) => void;

const initialState: ToastState = {
    toasts: [],
    lastToastAction: null,
};

const listeners = new Set<Listener>();
let state: ToastStore;

const getState = (): ToastStore => state;

const subscribe = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const setState: SetState = partial => {
    const next = typeof partial === 'function' ? partial(state) : partial;

    if (Object.is(next, state)) {
        return;
    }

    const hasChanges = keys(next).some(key => !Object.is(state[key], next[key]));

    if (!hasChanges) {
        return;
    }

    const prevState = state;
    state = { ...state, ...next };
    listeners.forEach(listener => listener(state, prevState));
};

state = {
    ...initialState,
    add: ({ toast, toastAction }) => {
        setState({
            /**
             * Currently we don't support stacking toasts, dismissing other existing
             * toasts instantly, so existing toast exit animation would not overlap
             * with new toast entering animation
             */
            toasts: [{ ...toast, id: uuid(), isVisible: true }],
            lastToastAction: toastAction || null,
        });
    },
    remove: id => {
        setState(currentState => ({
            toasts: currentState.toasts.filter(t => t.id !== id),
            lastToastAction: null,
        }));
    },
};

const initialStoreState: ToastStore = state;

const getInitialState = (): ToastStore => initialStoreState;

const useToastStore = Object.assign(
    <TSelected>(selector: (state: ToastStore) => TSelected): TSelected =>
        useSyncExternalStore(
            subscribe,
            () => selector(state),
            () => selector(initialStoreState)
        ),
    { getState, getInitialState, setState, subscribe }
);

export { useToastStore };
